import { SimManagementError } from '../models/Errors';
import { UserSettings } from '../models/state/userSettings';
import { Action } from './';
import {
  DELETE_USER_SETTINGS,
  DELETE_USER_SETTINGS_FAILURE,
  DELETE_USER_SETTINGS_SUCCESS,
  ENSURE_USER_SETTINGS,
  LOAD_USER_SETTINGS,
  LOAD_USER_SETTINGS_FAILURE,
  LOAD_USER_SETTINGS_PENDING,
  LOAD_USER_SETTINGS_SUCCESS,
  PUT_USER_SETTINGS,
  PUT_USER_SETTINGS_FAILURE,
  PUT_USER_SETTINGS_SUCCESS,
} from './actionTypes';
import { ActionMeta, PromiseAction, RejectFunc, ResolveFunc } from './commonActions';

/**
 * Action which is dispatched when user settings are deleted.
 * @param id The identifier of the user.
 * @param resolve Optional function which will be called on success.
 * @param reject Optional function which will be called on failure.
 */
export function deleteUserSettings(
  id: string,
  resolve?: ResolveFunc<UserSettings>,
  reject?: RejectFunc
): PromiseAction<string, UserSettings> {
  return {
    type: DELETE_USER_SETTINGS,
    payload: id,
    resolve,
    reject,
  };
}

/**
 * Action which is dispached when user settings are successfully deleted from the server.
 * @param userSettings The user settings that were deleted.
 */
export function deleteUserSettingsSuccess(userSettings: UserSettings): Action<UserSettings> {
  return {
    type: DELETE_USER_SETTINGS_SUCCESS,
    payload: userSettings,
  };
}

/**
 * Action which is dispatched when a spend cap fails to be deleted.
 * @param error The error which caused the action.
 */
export function deleteUserSettingsFailure(
  error: SimManagementError,
  userSettings: UserSettings
): ActionMeta<SimManagementError, UserSettings> {
  return {
    error: true,
    type: DELETE_USER_SETTINGS_FAILURE,
    payload: error,
    meta: userSettings,
  };
}

/**
 * Action which ensures that the user settings are loaded and will fetch from server if it is not.
 * @param id The user identifier.
 */
export function ensureUserSettings(id: string): Action<string> {
  return {
    type: ENSURE_USER_SETTINGS,
    payload: id,
  };
}

/**
 * Action which loads the selected user settings from the server.
 * @param id The user identifier.
 */
export function loadUserSettings(id: string): Action<string> {
  return {
    type: LOAD_USER_SETTINGS,
    payload: id,
  };
}

/**
 * Action which indicates that the user settings are being loaded.
 * @param payload
 */
export function loadUserSettingsPending(payload: string): Action<string> {
  return {
    type: LOAD_USER_SETTINGS_PENDING,
    payload,
  };
}

/**
 * Action which handles when there is a failure to load the user settings
 * @param id The user identifier.
 * @param error The error.
 */
export function loadUserSettingsFailure(id: string, error: SimManagementError): ActionMeta<SimManagementError, string> {
  return {
    error: true,
    type: LOAD_USER_SETTINGS_FAILURE,
    payload: error,
    meta: id,
  };
}

/**
 * Action which handles when the user settings are successfully loaded.
 * @param userSettings The spend cap.
 */
export function loadUserSettingsSuccess(userSettings: UserSettings): Action<UserSettings> {
  return {
    type: LOAD_USER_SETTINGS_SUCCESS,
    payload: userSettings,
  };
}

export function putUserSettings(
  payload: UserSettings,
  resolve?: ResolveFunc<UserSettings>,
  reject?: RejectFunc
): PromiseAction<UserSettings, UserSettings> {
  return {
    type: PUT_USER_SETTINGS,
    payload,
    resolve,
    reject,
  };
}

/**
 * Action which is dispatched when a dashboard has been successfully created.
 * @param dashboard The dashboard which was created.
 */
export function putUserSettingsSuccess(userSettings: UserSettings): Action<UserSettings> {
  return {
    type: PUT_USER_SETTINGS_SUCCESS,
    payload: userSettings,
  };
}

/**
 * Action which is dispatched on failing to put user settings to the server.
 * @param error The error which caused the action.
 */
export function putUserSettingsFailure(
  error: SimManagementError,
  userSettings: UserSettings
): ActionMeta<SimManagementError, UserSettings> {
  return {
    error: true,
    type: PUT_USER_SETTINGS_FAILURE,
    payload: error,
    meta: userSettings,
  };
}
